import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import ReactSelect from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@material-ui/core";
import { Button } from "react-bootstrap";
import RemoveCircleOutlineOutlinedIcon from "@material-ui/icons/RemoveCircleOutlineOutlined";
import AddCircleOutlineOutlinedIcon from "@material-ui/icons/AddCircleOutlineOutlined";
import { selectRequestSubTypesById } from "../../../../../redux/actions/lenderRequestActions";
import ReasonCodeTooltip from "../../../../SharedComponents/ReasonCodeTooltip/ReasonCodeTooltip";
import ReasonCodeSelector from "../Components/ReasonCodeSelector";
import { REQUEST_TYPES } from "../../../../shared/constants/RequestTypes";
import { isEquivalent } from "../../../../shared/utilities/array-utils";

import "../CreateStatusReviewRequest.css";

const SUMMARY_ANALYSIS_MAX_LENGTH = 2000;

// Compares current reason code selections against previously-submitted formState
// to detect whether subsequent step state needs clearing.
// NOTE: This is separate from hasReasonCodeChanges (which validates against project's existing codes).
export const haveFormReasonCodesChanged = (newCodes, previousCodes) => {
  if (!previousCodes) return false;
  if (!newCodes) return previousCodes.length > 0;
  return !isEquivalent(newCodes, previousCodes, rc => rc.value);
};

const RequestDetailsPage = ({
  projectInfo,
  formState,
  incrementCounter,
  decrementCounter,
  projectId
}) => {
  const { handleSubmit, setValue, watch, register } = useForm({
    defaultValues: {
      addUnavailable: false,
      selectedReasonCodes: null,
      comments: ""
    }
  });

  const [loading, setLoading] = useState(false);
  const [allReasonCodes, setAllReasonCodes] = useState([]);
  const [existingReasonCodes, setExistingReasonCodes] = useState([]);
  const [reasonCodeOptions, setReasonCodeOptions] = useState([]);
  const [showCommentValidation, setShowCommentValidation] = useState(false);
  const [showReasonCodeValidation, setShowReasonCodeValidation] = useState(false);
  const [canContinue, setCanContinue] = useState(false);
  const [reasonCodeErrorMessage, setReasonCodeErrorMessage] = useState("");
  // Modify Unavailable Status only: existing codes still kept vs. newly added codes
  const [keptReasonCodes, setKeptReasonCodes] = useState([]);
  const [newReasonCodes, setNewReasonCodes] = useState([]);
  // ponytail: guards the derive-effect below from firing (and clobbering the restored
  // formState.selectedReasonCodes) before the async existing-codes seed has run.
  const [reasonCodesSeeded, setReasonCodesSeeded] = useState(false);

  // get reason code to requestSubTypeId lookup from store
  const reasonCodeToRequestSubTypeLookup = useSelector(state => state.lenderRequestReducer.reasonCodeToRequestSubTypeLookup);

  // Watch form values
  const watchedAddUnavailable = watch("addUnavailable");
  const watchedReasonCodes = watch("selectedReasonCodes");
  const watchedComments = watch("comments");

  // Get current request type from form state
  const requestType = formState?.requestType;

  const dispatch = useDispatch();  

  // Restore form state if returning to this step
  useEffect(() => {
    if (formState) {
      if (formState.addUnavailable !== undefined) {
        setValue("addUnavailable", formState.addUnavailable);
      }
      if (formState.selectedReasonCodes) {
        setValue("selectedReasonCodes", formState.selectedReasonCodes);
      }
      if (formState.comments) {
        setValue("comments", formState.comments);
      }
    }
  }, [formState, setValue]);

  useEffect(() => {
    if (allReasonCodes.length && !existingReasonCodes.length &&
      (requestType === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS || requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS)
    ) {
      (async () => {
        const _existingReasonCodes = await getExistingReasonCodes(allReasonCodes);
        setExistingReasonCodes(_existingReasonCodes);
        setShowReasonCodeValidation(_existingReasonCodes?.length > 0);

        // For MODIFY_UNAVAILABLE_STATUS, seed keptReasonCodes/newReasonCodes: restore the
        // kept/added split from a previously-submitted formState, else default to all-kept.
        if (requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && _existingReasonCodes.length) {
          if (formState?.selectedReasonCodes) {
            const selectedIds = new Set(formState.selectedReasonCodes.map(rc => rc.value));
            setKeptReasonCodes(_existingReasonCodes.filter(code => selectedIds.has(code.reasonId)));
            setNewReasonCodes(formState.selectedReasonCodes.filter(
              rc => !_existingReasonCodes.some(code => code.reasonId === rc.value)
            ));
          } else {
            setKeptReasonCodes(_existingReasonCodes);
          }
        }
        setReasonCodesSeeded(true);
      })();
    }
  }, [requestType, allReasonCodes, formState, setValue]);

  // Modify Unavailable Status only: keep the react-hook-form "selectedReasonCodes" value
  // (used by canContinue/validation/submit) in sync with the kept + added lists.
  useEffect(() => {
    if (requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && reasonCodesSeeded) {
      const derivedReasonCodes = [
        ...keptReasonCodes.map(code => ({ label: code.reasonCode, value: code.reasonId })),
        ...newReasonCodes
      ];
      setValue("selectedReasonCodes", derivedReasonCodes);
      validateReasonCodes(derivedReasonCodes);
    }
  }, [keptReasonCodes, newReasonCodes, requestType, reasonCodesSeeded]);

  useEffect(() => {
    if (requestType) {
      if (requestType === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS) {
        setCanContinue(!!watchedComments?.trim());
      } else if (watchedReasonCodes != null) {
        if (requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS) {
          setCanContinue(watchedReasonCodes.length && !!watchedComments?.trim());
        } else if (requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS) {
          setCanContinue(watchedReasonCodes.length && hasReasonCodeChanges(watchedReasonCodes, existingReasonCodes) && !!watchedComments?.trim());
        } else {
          console.warn("Unknown request type for reason code validation:", requestType);
        }
      }
    }
  }, [requestType, watchedReasonCodes, watchedComments]);

  // Fetch reason codes 
  useEffect(() => {
    const fetchData = async () => {
      try {
        const sortedReasonCodes = await getAllReasonCodes();

        setAllReasonCodes(sortedReasonCodes);
        
        const options = sortedReasonCodes.map(rc => ({
          label: rc.reasonCode,
          value: rc.reasonId
        }));

        setReasonCodeOptions(options);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [projectId]);

  const getAllReasonCodes = async () => {
    const reasonCodesResponse = await axios.get(
      `${global.REACT_APP_API_ENDPOINT}/reasonCodes/getAllReasonCodes`
    );

    const sortedReasonCodes = 
      (reasonCodesResponse.data || [])
        .filter(r => r.active)
        .sort(
          (a, b) => a.reasonCode.localeCompare(b.reasonCode)
        );

    return sortedReasonCodes;
  };

  const getExistingReasonCodes = async (allReasonCodes) => {
    try {
      setLoading(true);

      // Get FM Activity Feed data like Project.js does
      const { data } = await axios.get(
        global.REACT_APP_API_ENDPOINT +
        `/activityFeed/getFnmaReviewStatusActivityFeedForProject/` + projectId
      );
      
      const fmActivity = data.activityFeedFnmaStatusDTO || [];

      // Find the most recent entry
      const latestEntry = fmActivity[0];
      
      if (latestEntry?.reasonIds && latestEntry?.fnmaReviewStatus === 'Unavailable') {
        // Use already-fetched reason codes to map the IDs to full reason code objects
        const existingCodes = latestEntry.reasonIds
          .map(id => allReasonCodes.find(code => code.reasonId === id))
          .filter(Boolean) 
          .sort((a, b) => a.reasonCode.localeCompare(b.reasonCode));
        
        return existingCodes;
      }
      
      return [];

    } catch (err) {
      console.error(err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleReasonCodeChange = (selectedOptions) => {
    setValue("selectedReasonCodes", selectedOptions || []);
    validateReasonCodes(selectedOptions);
  };

  // Modify Unavailable Status only: existing codes dropped from keptReasonCodes, and new
  // codes chosen via the Add control (ReasonCodeSelector), derived fresh each render.
  const removedReasonCodesList = requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
    ? existingReasonCodes.filter(code => !keptReasonCodes.some(kept => kept.reasonId === code.reasonId))
    : [];
  const hasReasonCodeNetChange = removedReasonCodesList.length > 0 || newReasonCodes.length > 0;
  // Excludes reason codes already on the project so they can't be re-added via the dropdown.
  const addReasonCodeOptions = reasonCodeOptions.filter(
    option => !existingReasonCodes.some(code => code.reasonId === option.value)
  );

  // Checkbox is checked when a code is marked for removal (i.e. no longer in keptReasonCodes).
  const handleToggleRemoveReasonCode = (code) => {
    setKeptReasonCodes(prev => {
      const isKept = prev.some(kept => kept.reasonId === code.reasonId);
      if (isKept) {
        return prev.filter(kept => kept.reasonId !== code.reasonId);
      }
      return [...prev, code].sort((a, b) => a.reasonCode.localeCompare(b.reasonCode));
    });
  };

  const handleAddReasonCodesChange = (selectedOptions) => {
    setNewReasonCodes(selectedOptions || []);
  };

  const validateReasonCodes = (selectedReasonCodes) => {
    let validationMessage = "";

    if (requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS) {
      if (!selectedReasonCodes?.length) {
        validationMessage = "At least one reason code is required."
      } else if (!hasReasonCodeChanges(selectedReasonCodes, existingReasonCodes)) {
        validationMessage = "You must change at least one reason code to submit this request.";
      } else {
        validationMessage = "";
      }
    } else if (requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS) {
      if (!selectedReasonCodes?.length) {
        validationMessage = "At least one reason code is required.";
      } else {
        validationMessage = "";
      }
    }

    setReasonCodeErrorMessage(validationMessage);
    return !validationMessage;
  };

  // Checks whether the user has made any changes to the reason codes (vs the project's existing codes).
  // NOTE: This is separate from haveFormReasonCodesChanged (which detects drift between form submissions).
  const hasReasonCodeChanges = (selectedReasonCodes, existingReasonCodes) => {
    if (!selectedReasonCodes || !existingReasonCodes) {
      return false;
    }
    
    const selectedIds = new Set(selectedReasonCodes.map(rc => rc.value));
    const existingIds = new Set(existingReasonCodes.map(rc => rc.reasonId));
    
    if (selectedIds.size !== existingIds.size) {
      return true;
    }
    
    return ![...selectedIds].every((id) => existingIds.has(id));
  };

  const handleFormSubmit = (data) => {
    setShowCommentValidation(true);
    setShowReasonCodeValidation(true);

    const trimmedComments = (data.comments || "").trim();

    if (validateReasonCodes(data.selectedReasonCodes) && trimmedComments) {
      // Detect if reason codes have changed from previous submission
      const reasonCodesChanged = haveFormReasonCodesChanged(
        data.selectedReasonCodes,
        formState?.selectedReasonCodes
      );

      const formStateData = {
        ...formState,
        addUnavailable: data.addUnavailable,
        selectedReasonCodes: data.selectedReasonCodes,
        existingReasonCodes: existingReasonCodes,
        comments: trimmedComments,
      };

      if (requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS) {
        formStateData.removedReasonCodes = removedReasonCodesList.map(rc => rc.reasonCode);
        formStateData.addedReasonCodesList = newReasonCodes.map(rc => rc.label);
      }

      // Clear subsequent step state if reason codes changed
      if (reasonCodesChanged) {
        delete formStateData.selectedFiles;
        delete formStateData.requiredDocuments;
      }
      
      // Calculate reason code IDs for document requirements
      let reasonCodeIds;

      if (requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS) {
        // For Modify Unavailable, only include documents for added and removed reason codes
        // (codes that are unchanged do not require new documents)
        const selectedIds = new Set((formStateData.selectedReasonCodes || []).map(rc => rc.value));
        const existingIds = new Set((existingReasonCodes || []).map(rc => rc.reasonId));

        // Added codes: in selected but not in existing
        const addedIds = [...selectedIds].filter(id => !existingIds.has(id));
        // Removed codes: in existing but not in selected
        const removedIds = [...existingIds].filter(id => !selectedIds.has(id));

        reasonCodeIds = [...addedIds, ...removedIds];
      } else if (requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS) {
        reasonCodeIds = (formStateData.selectedReasonCodes || []).map(rc => rc.value);
      } else {
        // Remove Unavailable - use existing codes
        reasonCodeIds = (existingReasonCodes || []).map(rc => rc.reasonId);
      }
      
      const requestSubTypeIds = reasonCodeIds.map(rc => reasonCodeToRequestSubTypeLookup.get(rc)).filter(id => id != null);
      
      dispatch(selectRequestSubTypesById(requestSubTypeIds));

      incrementCounter(formStateData);
    }
  };

  const handleBack = () => {
    const currentData = {
      ...formState,
      addUnavailable: watchedAddUnavailable,
      selectedReasonCodes: watchedReasonCodes,
      comments: watchedComments
    };
    decrementCounter(currentData);
  };

  return (
    <Grid item xs={12} className="request-details-page-width">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid item xs={12}>
          <h1>Request Details</h1>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <h6>Step 2 of 5 - Request Configuration</h6>
          <p><strong>Request Type:</strong> {requestType}</p>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className="form-section reason-codes">
              <h3>
                Reason Codes
                {allReasonCodes?.length ? <ReasonCodeTooltip reasonCodes={allReasonCodes} /> : <></>}
              </h3>
              {!allReasonCodes?.length || loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  Loading reason codes...
                </div>
              ) : requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS ? (
                <>
                  <p>
                    Select the reason codes that apply to this request:
                    <span style={{ color: "red" }}>*</span>
                  </p>
                  <div className="reason-codes-container">
                    <ReactSelect
                      name="selectedReasonCodes"
                      className="pers-reason-codes"
                      options={reasonCodeOptions}
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      value={watchedReasonCodes || []}
                      onChange={handleReasonCodeChange}
                      allowSelectAll={false}
                      placeholder="Select reason codes..."
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: "45px",
                          borderColor:
                            showReasonCodeValidation && reasonCodeErrorMessage
                              ? "#d02f2f"
                              : state.isFocused
                              ? "#007bff"
                              : "#e6e8ed"
                        })
                      }}
                    />
                  </div>
                  {showReasonCodeValidation && reasonCodeErrorMessage && (
                    <div className="validation-error">
                      {reasonCodeErrorMessage}
                    </div>
                  )}
                </>
              ) : requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS ? (
                <>
                  <p>
                    Select the reason codes to remove and/or add for this project. At least one reason code is required.
                    <span style={{ color: "red" }}>*</span>
                  </p>
                  <div className="reason-codes-columns">
                    <div className="reason-codes-column reason-codes-column-remove">
                      <h4 className="reason-codes-column-title remove">
                        <RemoveCircleOutlineOutlinedIcon fontSize="small" />
                        Unavailable Reasons to Remove
                      </h4>
                      <p className="reason-codes-column-subtitle">
                        Select existing reason codes that should be removed.
                      </p>
                      {existingReasonCodes.length ? (
                        <>
                          <div className="reason-codes-table-label">Existing Reason Codes</div>
                          <div className="reason-codes-table-wrapper">
                            <table className="reason-codes-table">
                              <thead>
                                <tr>
                                  <th className="reason-codes-table-checkbox-col" />
                                  <th>Reason Code</th>
                                </tr>
                              </thead>
                              <tbody>
                                {existingReasonCodes.map((code) => {
                                  const markedForRemoval = !keptReasonCodes.some(
                                    kept => kept.reasonId === code.reasonId
                                  );
                                  return (
                                    <tr key={code.reasonId}>
                                      <td className="reason-codes-table-checkbox-col">
                                        <input
                                          type="checkbox"
                                          checked={markedForRemoval}
                                          onChange={() => handleToggleRemoveReasonCode(code)}
                                          aria-label={`Remove ${code.reasonCode}`}
                                        />
                                      </td>
                                      <td>{code.reasonCode}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div className="reason-codes-table-footer">
                            <span className="reason-codes-selected-count">
                              Selected <span className="reason-codes-selected-badge">{removedReasonCodesList.length}</span>
                            </span>
                            <button
                              type="button"
                              className="reason-codes-clear-link"
                              onClick={() => setKeptReasonCodes(existingReasonCodes)}
                            >
                              Clear selection
                            </button>
                          </div>
                        </>
                      ) : (
                        <p className="no-reason-codes-message">No existing reason codes to remove.</p>
                      )}
                    </div>

                    <div className="reason-codes-column reason-codes-column-add">
                      <h4 className="reason-codes-column-title add">
                        <AddCircleOutlineOutlinedIcon fontSize="small" />
                        Unavailable Reasons to Add
                      </h4>
                      <p className="reason-codes-column-subtitle">
                        Select new reason codes to add for this project.
                      </p>
                      <ReasonCodeSelector
                        reasonCodeOptions={addReasonCodeOptions}
                        selectedReasonCodes={newReasonCodes}
                        onChange={handleAddReasonCodesChange}
                        placeholder="Select reason codes to add..."
                      />
                      <div className="reason-codes-table-footer">
                        <span className="reason-codes-selected-count">
                          Selected <span className="reason-codes-selected-badge">{newReasonCodes.length}</span>
                        </span>
                        <button
                          type="button"
                          className="reason-codes-clear-link"
                          onClick={() => setNewReasonCodes([])}
                        >
                          Clear selection
                        </button>
                      </div>
                    </div>
                  </div>

                  {hasReasonCodeNetChange && (
                    <div className="reason-codes-summary">
                      <h4>Summary of Changes</h4>
                      <div className="reason-codes-summary-columns">
                        <div className="reason-codes-summary-box remove">
                          <div className="reason-codes-summary-box-title">
                            {removedReasonCodesList.length} Reason Code{removedReasonCodesList.length === 1 ? "" : "s"} to Remove
                          </div>
                          {removedReasonCodesList.length > 0 && (
                            <ul>
                              {removedReasonCodesList.map(rc => (
                                <li key={rc.reasonId}>{rc.reasonCode}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div className="reason-codes-summary-box add">
                          <div className="reason-codes-summary-box-title">
                            {newReasonCodes.length} Reason Code{newReasonCodes.length === 1 ? "" : "s"} to Add
                          </div>
                          {newReasonCodes.length > 0 && (
                            <ul>
                              {newReasonCodes.map(rc => (
                                <li key={rc.value}>{rc.label}{rc.description ? ` - ${rc.description}` : ""}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {showReasonCodeValidation && reasonCodeErrorMessage && (
                    <div className="validation-error">
                      {reasonCodeErrorMessage}
                    </div>
                  )}
                </>
              ) : existingReasonCodes?.length ? (
                <>
                  <p>
                    Fannie Mae will require documentation based on the existing reason codes associated with this project:
                  </p>
                  <ul className="reason-codes-list">
                    {existingReasonCodes.map((rc) => (
                      <li key={rc.reasonId}>{rc.reasonCode}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="no-reason-codes-message">
                  Note: No existing reason codes were found for this project's current Unavailable status.
                </p>
              )}
            </div>
            <div className="form-section comments">
              <h3>Summary & Analysis<span style={{ color: "red" }}>*</span></h3>
              <p>Provide a summary and analysis for this request:</p>
              <textarea
                {...register("comments")}
                placeholder="Enter your summary and analysis here..."
                maxLength={SUMMARY_ANALYSIS_MAX_LENGTH}
                className="comments-textarea"
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                {showCommentValidation && !watchedComments?.trim() && (
                  <div className="validation-error" style={{ margin: 0 }}>Summary & Analysis is required.</div>
                )}
                <div style={{ marginLeft: "auto", fontSize: "12px", color: "#666" }}>
                  {(watchedComments?.length || 0)}/{SUMMARY_ANALYSIS_MAX_LENGTH}
                </div>
              </div>
            </div>
          </Grid>
        </Grid>

        <Grid container spacing={2} className="dialoguClass">
          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Button
                type="submit"
                className="back-button"
                style={{width: "150px"}}
                onClick={handleBack}
                form="addProjectForm"
              >
                Back
              </Button>
              <Button
                type="submit"
                className={"btn-submit" + (!canContinue ? " btn-invalid" : "")}
              >
                Next
              </Button>            
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};

export default RequestDetailsPage;